import { Link } from 'react-router-dom';
import { Logo } from './Logo';
import { cn } from '@/lib/cn';

const SHOP_LINKS = [
  { label: 'Women', to: '/category/women' },
  { label: 'Men', to: '/category/men' },
  { label: 'Kids', to: '/category/kids' },
  { label: 'Jewelry', to: '/category/jewelry' },
  { label: 'Bags', to: '/category/bags' },
  { label: 'Accessories', to: '/category/accessories' },
];

const HELP_LINKS = [
  { label: 'Contact', to: '/contact' },
  { label: 'Track Order', to: '/orders' },
  { label: 'Returns', to: '/returns' },
  { label: 'Shipping', to: '/shipping' },
  { label: 'Size Guide', to: '/size-guide' },
];

const COMPANY_LINKS = [
  { label: 'About', to: '/about' },
  { label: 'Careers', to: '/careers' },
  { label: 'Privacy', to: '/privacy' },
  { label: 'Terms', to: '/terms' },
];

const SOCIAL = [
  { label: 'Instagram', href: 'https://instagram.com/quickxkart', icon: 'IG' },
  { label: 'Twitter', href: 'https://twitter.com/quickxkart', icon: 'X' },
  { label: 'YouTube', href: 'https://youtube.com/@quickxkart', icon: 'YT' },
  { label: 'LinkedIn', href: 'https://linkedin.com/company/quickxkart', icon: 'IN' },
];

interface FooterColumnProps {
  title: string;
  items: { label: string; to: string }[];
}

function FooterColumn({ title, items }: FooterColumnProps) {
  return (
    <div>
      <h3
        className="text-label-mono mb-4"
        style={{ color: 'var(--color-gold)' }}
      >
        {title}
      </h3>
      <ul className="space-y-2.5">
        {items.map((item) => (
          <li key={item.to}>
            <Link
              to={item.to}
              className="group inline-flex items-center gap-2 text-[14px] text-[color:var(--color-text-secondary)] transition-colors hover:text-white"
            >
              <span className="relative">
                {item.label}
                <span
                  aria-hidden
                  className="absolute -bottom-0.5 left-0 h-px w-0 bg-[color:var(--color-brand-magenta)] transition-[width] duration-300 group-hover:w-full"
                />
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

interface FooterProps {
  className?: string;
}

/**
 * Site footer (PRD §1, brand statement). Four-column grid on desktop, stacks
 * on mobile. Above the fold sits the brand mark + tagline + social row;
 * below: shop / help / company link columns; bottom: copyright + legal.
 *
 * Newsletter capture (PRD §7.1.9) lives on the Home page itself, not in the
 * footer — keeping the global footer content stable across all routes.
 */
export function Footer({ className }: FooterProps) {
  const year = new Date().getFullYear();

  return (
    <footer
      className={cn('relative overflow-hidden border-t border-white/[0.06] mt-24', className)}
      style={{ background: 'rgba(5, 6, 26, 0.85)', backdropFilter: 'blur(12px)' }}
    >
      {/* Hairline gold accent — the festive thread */}
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 h-px"
        style={{
          background:
            'linear-gradient(90deg, transparent 0%, rgba(200,150,62,0.45) 50%, transparent 100%)',
        }}
      />

      {/* Centered QuickXKart brand signature behind the footer content —
       *  editorial italic, soft fill, scaled to fit the viewport. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-center overflow-hidden"
      >
        <span
          className="select-none whitespace-nowrap text-center font-[family-name:var(--font-editorial)] italic leading-[0.85] tracking-[-0.02em]"
          style={{
            fontSize: 'clamp(72px, 13vw, 200px)',
            color: 'rgba(255,255,255,0.045)',
            fontWeight: 500,
            transform: 'translateY(10%)',
          }}
        >
          QuickXKart
        </span>
      </div>

      <div className="container-q relative z-10 py-16">
        {/* Brand row */}
        <div className="flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
          <div className="max-w-md">
            <Logo variant="full" />
            <p
              className="mt-6 text-[15px] leading-relaxed"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              100,000+ styles, Indian craft, global edge. Festive futurism for
              the way you dress, work, and live.
            </p>
          </div>

          <div className="flex flex-col gap-3">
            <span
              className="text-label-mono"
              style={{ color: 'var(--color-text-muted)' }}
            >
              Follow the brand
            </span>
            <div className="flex gap-2">
              {SOCIAL.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  rel="noopener noreferrer"
                  target="_blank"
                  className="glass-card flex h-11 w-11 items-center justify-center text-[12px] font-[family-name:var(--font-label)] tracking-[0.15em] text-white transition-transform hover:scale-105 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-brand-magenta)]"
                >
                  {/* Visible 2-letter mark is decorative; the sr-only text is
                      what screen readers + voice control read out. This avoids
                      the label/content mismatch (axe SC 2.5.3). */}
                  <span aria-hidden>{s.icon}</span>
                  <span className="sr-only">{s.label}</span>
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Link grid */}
        <div
          className="mt-14 grid gap-10 sm:grid-cols-2 md:grid-cols-4"
          style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '40px' }}
        >
          <div>
            <h3
              className="text-label-mono mb-4"
              style={{ color: 'var(--color-gold)' }}
            >
              QuickXKart
            </h3>
            <p
              className="text-[13px] leading-relaxed"
              style={{ color: 'var(--color-text-muted)' }}
            >
              🇮🇳 Made in India. Shipped across India.
              <br />
              COD available · Easy 7-day returns · Free shipping above ₹999.
            </p>
          </div>
          <FooterColumn title="Shop" items={SHOP_LINKS} />
          <FooterColumn title="Help" items={HELP_LINKS} />
          <FooterColumn title="Company" items={COMPANY_LINKS} />
        </div>

        {/* Legal row */}
        <div
          className="mt-12 flex flex-col items-start justify-between gap-3 pt-6 sm:flex-row sm:items-center"
          style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}
        >
          <p
            className="text-[12px]"
            style={{ color: 'var(--color-text-muted)' }}
          >
            © {year} QuickXKart Private Limited. All rights reserved.
          </p>
          <p
            className="text-label-mono"
            style={{ color: 'var(--color-text-muted)' }}
          >
            Crafted with{' '}
            <span style={{ color: 'var(--color-brand-magenta)' }}>♥</span> in India
          </p>
        </div>
      </div>
    </footer>
  );
}
